import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import InstallPrompt from "./components/InstallPrompt";
import SplashScreen from "./components/SplashScreen";
import PersistentSession from "./components/PersistentSession";
import PushRegistration from "./components/PushRegistration";
import AppBackNavigation from "./components/AppBackNavigation";
import DeviceClockNotice from "./components/DeviceClockNotice";
import "./App.css";
import "./pages/dashboard.css";
import "./pages/Admin/GuestStudents.css";
import "./pages/Admin/sessions.css";
import "./pages/advisor/plan-requests.css";
import "./pages/advisor/dailyReport.css";
import "./pages/advisor/send-plan.css";
import "./pages/advisor/advisor_create_session.css";
import "./pages/advisor/advisor_sessions.css";
import "./pages/student/exam-results.css";
import "./styles/filter-controls.css";

// Lazy-loaded components
const Login = lazy(() => import("./components/Login"));
const Register = lazy(() => import("./components/Register"));
const VerifyOtp = lazy(() => import("./components/VerifyOtp"));
const WaitingForApproval = lazy(() => import("./components/WaitingForApproval"));
const SetPassword = lazy(() => import("./components/SetPassword"));

// Guest components
const GuestRegister = lazy(() => import("./components/GuestRegister"));
const GuestVerifyOtp = lazy(() => import("./components/GuestVerifyOtp"));
const GuestDashboard = lazy(() => import("./pages/GuestDashboard"));

// Dashboards
const AdvisorDashboard = lazy(() => import("./pages/AdvisorDashboard"));
const StudentDashboard = lazy(() => import("./pages/StudentDashboard"));
const LeaderDashboard = lazy(() => import("./pages/LeaderDashboard"));
const SupporterDashboard = lazy(() => import("./pages/SupporterDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ParentDashboard = lazy(() => import("./pages/ParentDashboard"));
const QuestionDesignerDashboard = lazy(() => import("./pages/QuestionDesignerDashboard"));

const SendPlanPage = lazy(() => import("./pages/advisor/SendPlanPage"));


// ---------------------- PRIVATE ROUTE ----------------------
function PrivateRoute({ children, allowed }) {
  const role = localStorage.getItem("role");

  if (!role) return <Navigate to="/auth/login" replace />;
  if (!allowed.includes(role)) return <Navigate to="/auth/login" replace />;

  return children;
}


// ---------------------- APP ROUTES ----------------------
function AppBackRouter({ children }) {
  const location = useLocation();
  return <AppBackNavigation pathname={location.pathname}>{children}</AppBackNavigation>;
}

function App() {
  return (
    <BrowserRouter>
      <AppBackRouter>
      <InstallPrompt />
      <PersistentSession />
      <PushRegistration />
      <DeviceClockNotice />

      <Suspense fallback={<SplashScreen />}>
        <Routes>
          <Route path="/" element={<Navigate to="/auth/login" replace />} />

          {/* Auth */}
          <Route path="/auth/login" element={<Login />} />
          <Route path="/auth/set-password" element={<SetPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Student */}
          <Route path="/student/register" element={<Register />} />
          <Route path="/student/verify" element={<VerifyOtp />} />
          <Route path="/student/waiting" element={<WaitingForApproval />} />
          <Route
            path="/student-dashboard"
            element={
              <PrivateRoute allowed={["student"]}>
                <StudentDashboard />
              </PrivateRoute>
            }
          />

          {/* Guest */}
          <Route path="/guest/register" element={<GuestRegister />} />
          <Route path="/guest/verify" element={<GuestVerifyOtp />} />
          <Route
            path="/guest/dashboard"
            element={
              <PrivateRoute allowed={["guest"]}>
                <GuestDashboard />
              </PrivateRoute>
            }
          />

          {/* Advisor */}
          <Route
            path="/advisor/dashboard"
            element={
              <PrivateRoute allowed={["advisor"]}>
                <AdvisorDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/advisor/send-plan/:request_id"
            element={
              <PrivateRoute allowed={["advisor"]}>
                <SendPlanPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/advisor/send-plan/student/:student_phone"
            element={
              <PrivateRoute allowed={["advisor"]}>
                <SendPlanPage />
              </PrivateRoute>
            }
          />

          {/* Supervisor */}
          <Route
            path="/supervisor/dashboard"
            element={
              <PrivateRoute allowed={["supervisor"]}>
                <LeaderDashboard />
              </PrivateRoute>
            }
          />

          {/* Supporter */}
          <Route
            path="/supporter/dashboard"
            element={
              <PrivateRoute allowed={["supporter"]}>
                <SupporterDashboard />
              </PrivateRoute>
            }
          />

          {/* Admin */}
          <Route
            path="/admin/dashboard"
            element={
              <PrivateRoute allowed={["admin"]}>
                <AdminDashboard />
              </PrivateRoute>
            }
          />

          {/* Parent */}
          <Route
            path="/parent/dashboard"
            element={
              <PrivateRoute allowed={["parent"]}>
                <ParentDashboard />
              </PrivateRoute>
            }
          />

          {/* Question designer */}
          <Route
            path="/question-designer/dashboard"
            element={
              <PrivateRoute allowed={["question_designer"]}>
                <QuestionDesignerDashboard />
              </PrivateRoute>
            }
          />

          {/* 404 */}
          <Route
            path="*"
            element={
              <main className="app-not-found"><div><span>۴۰۴</span><h1>صفحه پیدا نشد</h1><p>آدرس واردشده معتبر نیست یا صفحه جابه‌جا شده است.</p><a href="/auth/login">بازگشت به سامانه</a></div></main>
            }
          />
        </Routes>
      </Suspense>
      </AppBackRouter>
    </BrowserRouter>
  );
}

export default App;
